import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { listOrphanItcEntries } from '@/api/runs';
import type { OrphanItcEntryOut } from '@/api/types';
import type { Currency } from '@/lib/format';
import { ErrorCard } from '@/components/ui/error-card';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useAuth } from '../auth/AuthContext';
import { formatCurrency, formatDate } from '@/lib/format';

const inputClass =
  'rounded-md border border-input bg-background px-3 py-1.5 text-sm shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-brand-royal';

/** True when the query (case-insensitive) appears in any of the entry's
 *  displayed text fields. */
function matchesFilter(e: OrphanItcEntryOut, q: string): boolean {
  const hay = [
    e.transaction_external_id,
    e.gl_account_name,
    e.gl_account_code,
    e.entity,
    e.tax_code,
    e.memo,
  ];
  return hay.some((v) => v != null && v.toLowerCase().includes(q));
}

/** Orphan ITC entries tab — journal-entry lines that post a GST/HST
 *  amount to a tax account with no expense base on the same transaction
 *  (a missed HST booked as its own stray entry rather than an amendment
 *  to the original bill). Read-only surfacing for practitioner review; no
 *  matching to the taxable transaction that was missing tax (a later
 *  phase). Detector is provisional pending Andrew's confirmation. */
export function OrphanItcTab({ runId }: { runId: string }) {
  const { userKey, getIdToken } = useAuth();
  const [query, setQuery] = useState('');
  const entries = useQuery({
    queryKey: ['orphan-itc-entries', runId, userKey],
    queryFn: async () => listOrphanItcEntries(runId, (await getIdToken()) ?? ''),
  });

  const allRows = entries.data?.items;
  const rows = useMemo(() => {
    if (!allRows) return [];
    const needle = query.trim().toLowerCase();
    return needle ? allRows.filter((e) => matchesFilter(e, needle)) : allRows;
  }, [allRows, query]);

  if (entries.isPending) {
    return (
      <div className="py-8">
        <Spinner label="Loading orphan ITC entries…" />
      </div>
    );
  }
  if (entries.isError) {
    return <ErrorCard title="Couldn't load orphan ITC entries" error={entries.error} />;
  }

  // Zero candidates on the whole run — distinct from "filter matched nothing".
  if (allRows && allRows.length === 0) {
    return (
      <p className="text-sm text-brand-slate">
        No orphan ITC entries on this run — every tax-account posting has a
        matching expense base.
      </p>
    );
  }

  return (
    <div className="grid gap-3">
      <p className="text-sm text-brand-slate">
        Journal-entry lines posting GST/HST to a tax account with no expense
        base on the same transaction — likely a missed ITC booked as its own
        stray entry. Review only; not yet matched to the taxable transaction.
      </p>
      <input
        type="search"
        placeholder="Filter by transaction, GL account, entity, tax code, memo…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className={`${inputClass} max-w-md`}
        aria-label="Filter orphan ITC entries"
      />
      {rows.length === 0 ? (
        <p className="text-sm text-brand-slate">No entries match “{query.trim()}”.</p>
      ) : (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Date</TableHead>
            <TableHead>Transaction</TableHead>
            <TableHead>GL account</TableHead>
            <TableHead>Entity</TableHead>
            <TableHead>Tax code</TableHead>
            <TableHead className="text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((e, i) => (
            <TableRow key={`${e.transaction_external_id ?? 'na'}-${i}`}>
              <TableCell>{formatDate(e.posting_date)}</TableCell>
              <TableCell>{e.transaction_external_id ?? '—'}</TableCell>
              <TableCell>
                {e.gl_account_name ?? '—'}
                {e.gl_account_code ? (
                  <span className="block text-xs text-brand-slate">{e.gl_account_code}</span>
                ) : null}
                {e.memo ? (
                  <span className="block text-xs text-brand-slate" title={e.memo}>
                    {e.memo}
                  </span>
                ) : null}
              </TableCell>
              <TableCell>{e.entity ?? '—'}</TableCell>
              <TableCell>{e.tax_code ?? '—'}</TableCell>
              <TableCell className="text-right">
                {formatCurrency(e.amount, e.currency as Currency)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      )}
    </div>
  );
}
